/**
 * <RecordChaseCard> — Champion + Record-Chase pro Wertung
 * R102.7 (Stephan-Spec 2026-06-01)
 *
 * Zeigt für eine Kategorie (z.B. „Distance · Slope · 3 h") den aktuell
 * führenden Piloten mit Foto + Klarname + Flag + Klub, plus einen
 * Fortschrittsbalken zwischen den 2024- und 2025-Rekorden:
 *
 *   ┌───────────────────────────────────────────┐
 *   │  DISTANCE CHAMPION (BEST KM)              │
 *   │  151.55 km                                │
 *   │  [Avatar]  Dietz Harald 🇩🇪 (DE)           │
 *   │             Neustadt/Uffenheim            │
 *   │                                           │
 *   │  RECORD CHASE                       46%   │
 *   │  ━━━━━━●─────────────                     │
 *   │  2024: 279.60 km    2025: 330.06 km       │
 *   │  Source: Slope/overall/3hour              │
 *   └───────────────────────────────────────────┘
 */

'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { useScrollShadow } from '@/hooks/use-scroll-shadow'
import { WarningLamp } from '@/components/ui/warning-lamp'
import { useTranslation } from '@/i18n/client'

type RecordRow = {
  season_year: number
  terrain: string
  category: string
  value: number
  unit: string
  pilot_name: string
  country: string | null
  club: string | null
  description: string | null
}

export interface RecordChaseCardProps {
  /** SeasonRecord.category — z. B. "30" / "1" / "overall" / "club" / "distance" */
  category: string
  /** "flatland" oder "slope" */
  terrain: 'flatland' | 'slope'
  /** Titel-Zeile oben — z. B. "DISTANCE CHAMPION (BEST KM)" */
  title: string
  /** Quellen-Label unten (klein, dezent, ab R102.7.3 nicht mehr gerendert) */
  sourceLabel?: string
  /** Aktueller Top-1-Pilot (volle Pilot-Card-Daten) */
  currentLeader?: {
    name: string
    avatarUrl?: string | null
    countryCode?: string | null
    clubName?: string | null
    slug?: string | null
    isPublic?: boolean
  } | null
  /** Aktueller Score-Wert (km oder Pkt) */
  currentValue?: number | null
  /** Einheit (km / pts) */
  currentUnit?: 'km' | 'pts'
}

function fmtNum(value: number, unit: 'km' | 'pts'): string {
  if (unit === 'km') return `${value.toLocaleString('de-DE', { maximumFractionDigits: 2 })} km`
  return `${value.toLocaleString('de-DE', { maximumFractionDigits: 0 })} pts`
}

export function RecordChaseCard(props: RecordChaseCardProps) {
  const { category, terrain, title, sourceLabel, currentLeader, currentValue, currentUnit = 'km' } = props
  const { t } = useTranslation()
  const [records, setRecords] = useState<RecordRow[]>([])
  const [loading, setLoading] = useState(true)

  // R106.3 v2 (2026-06-07): Polling-basierter Scroll-Motion-Trail (Lenis-safe)
  const scrollShadow = useScrollShadow({ maxOffset: 18, maxOpacity: 0.7 })

  useEffect(() => {
    let aborted = false
    const params = new URLSearchParams()
    params.set('years', '2024,2025')
    params.set('category', category)
    params.set('terrain', terrain)
    fetch(`/api/public/records?${params.toString()}`)
      .then(r => (r.ok ? r.json() : { data: [] }))
      .then(j => { if (!aborted) setRecords(j.data ?? []) })
      .catch(() => { if (!aborted) setRecords([]) })
      .finally(() => { if (!aborted) setLoading(false) })
    return () => { aborted = true }
  }, [category, terrain])

  const r2024 = records.find(r => r.season_year === 2024)
  const r2025 = records.find(r => r.season_year === 2025)
  const unit = (r2025?.unit ?? r2024?.unit ?? currentUnit) as 'km' | 'pts'

  // R102.7 (Stephan 2026-06-01): Balken-Skala absolut 0 → 2025-Rekord (= 100 %).
  // 2024-Rekord als sichtbarer Marker auf dem Balken. Aktueller Wert ist die
  // gefüllte Balken-Breite. Tooltips zeigen Pilot + Differenz.
  const recordCeiling = r2025?.value ?? r2024?.value ?? null
  const currentPctOfCeiling = currentValue != null && recordCeiling && recordCeiling > 0
    ? (currentValue / recordCeiling) * 100
    : null
  const marker2024Pct = r2024 && r2025 && r2025.value > 0
    ? (r2024.value / r2025.value) * 100
    : null

  const beatsRecord = !!(currentValue && r2025 && currentValue > r2025.value)
  const beats2024  = !!(currentValue && r2024 && currentValue > r2024.value)

  // Differenzen für Tooltips
  const diffTo2024 = currentValue != null && r2024 ? currentValue - r2024.value : null
  const diffTo2025 = currentValue != null && r2025 ? currentValue - r2025.value : null
  const pctOf2024 = currentValue != null && r2024 && r2024.value > 0
    ? (currentValue / r2024.value) * 100
    : null

  const sourceFinal = sourceLabel || `${terrain}/${category}`

  function fmtDiff(d: number): string {
    const abs = fmtNum(Math.abs(d), unit)
    return d >= 0 ? `+${abs}` : `−${abs}`
  }

  // Tooltip-Texte (data-tip → globale Sprechblase)
  // R102.7.3 (Stephan-Wunsch 2026-06-01): Rekord-Tooltips enthalten Name + Land + Verein.
  // r19.55.220: komplett i18n mit Templated Keys wertung.recordProgressTip /
  // wertung.recordMarkerTip.
  function recordTooltip(rec: RecordRow | undefined, year: 2024 | 2025, diff: number | null): string {
    if (!rec) return ''
    const country = rec.country ? ` (${rec.country})` : ''
    const club = rec.club ? ` · ${rec.club}` : ''
    const deltaPart = diff != null
      ? (diff >= 0
          ? t('wertung.recordMarkerDeltaAbove', { delta: fmtDiff(diff) })
          : t('wertung.recordMarkerDeltaBelow', { delta: fmtDiff(-diff) }))
      : ''
    return t('wertung.recordMarkerTip', {
      year,
      value: fmtNum(rec.value, unit),
      name: rec.pilot_name,
      country,
      club,
      delta: deltaPart,
    })
  }
  const currentTooltip = currentValue != null
    ? t('wertung.recordProgressTip', {
        current: fmtNum(currentValue, unit),
        pct2024: pctOf2024 != null ? t('wertung.recordProgressPct2024', { pct: Math.round(pctOf2024) }) : '',
        pct2025: currentPctOfCeiling != null ? t('wertung.recordProgressPct2025', { pct: Math.round(currentPctOfCeiling) }) : '',
        delta2024: diffTo2024 != null ? t('wertung.recordProgressDelta2024', { delta: fmtDiff(diffTo2024) }) : '',
        delta2025: diffTo2025 != null ? t('wertung.recordProgressDelta2025', { delta: fmtDiff(diffTo2025) }) : '',
      })
    : ''
  const tooltip2024 = recordTooltip(r2024, 2024, diffTo2024)
  const tooltip2025 = recordTooltip(r2025, 2025, diffTo2025)

  return (
    <div
      className="rounded-[20px] relative"
      style={{
        background: 'linear-gradient(180deg, color-mix(in oklch, var(--award-gold) 5%, transparent), rgba(0,0,0,0.0))',
      }}
    >
      {/* Title — R106.3 (2026-06-07): Clash Display Variable Bold 700, Scroll-Motion-Trail
          r19.55.23 (2026-07-02): Light-Theme Kontrast — --hero-label-color statt Gold */}
      <div
        className="text-[10px] md:text-[11px] tracking-[.14em] uppercase mb-1"
        style={{
          color: 'var(--hero-label-color, var(--award-gold))',
          fontFamily: 'var(--font-display)',
          fontWeight: 700,
          textShadow: scrollShadow,
          transition: 'text-shadow 80ms linear',
        }}
      >
        {title}
      </div>

      {/* Current value (kompakt) — R106.3 (2026-06-07): Anton (--font-mega) Ultra-Heavy + Scroll-Trail
          r19.55.23 (2026-07-02): Light-Theme Kontrast — --hero-value-color statt Gold (auch bei beatsRecord) */}
      <div
        className="tabular-nums mb-2"
        style={{
          fontFamily: 'var(--font-mega)',
          fontWeight: 400, /* Anton ist single-weight, optisch Black */
          fontSize: 'clamp(28px, 3.8vw, 36px)', /* etwas größer, da Anton condensed */
          lineHeight: 1,
          letterSpacing: '0.01em',
          color: 'var(--hero-value-color, var(--award-gold))',
          textTransform: 'uppercase',
          textShadow: scrollShadow,
          transition: 'text-shadow 80ms linear',
        }}
      >
        {currentValue != null ? fmtNum(currentValue, unit) : <span style={{ color: 'var(--award-cirrus-3)' }}>—</span>}
      </div>

      {/* R102.7.7 (Stephan-Wunsch 2026-06-01 nachts): Champion-Row separat
          ENTFÄLLT — Avatar + Name + Flag wandern in die HUD-Pille auf dem Balken.
          Die HUD verfolgt die Rekord-Jagd visuell. */}

      {/* Record Chase Balken — R102.7.4 (Stephan-Wunsch 2026-06-01 spät):
          - RECORD CHASE als h2 OBEN zentriert (nicht im Rohr)
          - Pille insgesamt kompakter
          - Rekordhalter 2024 + 2025 namentlich unter dem Rohr
          - Breite gleich Top-10-Banner (kein extra padding nach links/rechts) */}
      <div>
        {/* R106.3 (2026-06-07): "Record Chase" Label entfernt — Stephan-Wunsch.
            HUD-Pille + Bar sprechen für sich selbst. */}

        {/* Marker-Container (Dreiecke + HUD-Pille OBERHALB des Rohrs).
            R102.7.7: 56px Höhe für die 48px HUD + 10px Pointer + Abstand. */}
        <div className="relative w-full" style={{ height: 62, marginBottom: 6 }}>
          {/* HUD-Pille als aktueller-Position-Marker — R102.7.7
              (Stephan-Wunsch 2026-06-01): „1 │ Avatar │ Name │ Flag" als
              wandernde Verfolger-HUD. Pulsiert + gold-conic-Spin.
              Position-Clamp: bleibt mit Mitte zwischen 12% und 88% damit
              die Pille die Card-Ränder nicht überragt. */}
          {currentPctOfCeiling != null && currentLeader && (() => {
            const canLink = !!currentLeader.isPublic && !!currentLeader.slug
            const clampedPct = Math.max(12, Math.min(88, currentPctOfCeiling))
            const hud = (
              <div
                className="fgl-rank-spin-gold"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  height: 48,
                  paddingLeft: 4,
                  paddingRight: 14,
                  borderRadius: 999,
                  background: 'var(--bg-card)',
                  lineHeight: 1,
                  fontFamily: 'var(--font-display)',
                  cursor: canLink ? 'pointer' : 'help',
                  maxWidth: 'min(360px, 60vw)',
                }}
              >
                {/* "1" Rank-Strip links */}
                <span
                  style={{
                    fontWeight: 900,
                    fontSize: 30,
                    color: 'var(--award-gold)',
                    letterSpacing: '-0.02em',
                    padding: '0 8px 0 6px',
                    borderRight: '1px solid color-mix(in oklch, var(--award-gold) 28%, transparent)',
                  }}
                >
                  1
                </span>
                {/* Avatar */}
                <div
                  className="rounded-full overflow-hidden shrink-0"
                  style={{
                    width: 36,
                    height: 36,
                    background: 'var(--award-ink-2)',
                    border: '1.5px solid color-mix(in oklch, var(--award-gold) 45%, transparent)',
                  }}
                >
                  {currentLeader.avatarUrl ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={currentLeader.avatarUrl}
                      alt={currentLeader.name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <div
                      className="w-full h-full flex items-center justify-center font-black"
                      style={{ color: 'var(--award-gold)', fontSize: 16 }}
                    >
                      {(currentLeader.name?.[0] || '?').toUpperCase()}
                    </div>
                  )}
                </div>
                {/* Name */}
                <span
                  className="truncate"
                  style={{
                    fontWeight: 800,
                    fontSize: 14,
                    color: canLink ? 'var(--award-gold)' : 'var(--award-cirrus-1)',
                    textTransform: 'uppercase',
                    letterSpacing: '-0.01em',
                    maxWidth: 200,
                  }}
                >
                  {currentLeader.name}
                </span>
                {/* Flag */}
                {currentLeader.countryCode && /^[a-z]{2}$/i.test(currentLeader.countryCode) && (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={`https://flagcdn.com/${currentLeader.countryCode.toLowerCase()}.svg`}
                    alt={currentLeader.countryCode.toUpperCase()}
                    style={{
                      height: 16,
                      width: 24,
                      borderRadius: 2,
                      objectFit: 'cover',
                      boxShadow: '0 0 0 1px rgba(0,0,0,0.18)',
                      flexShrink: 0,
                    }}
                  />
                )}
              </div>
            )
            return (
              <div
                className="absolute pointer-events-auto"
                data-tip={currentTooltip}
                style={{
                  left: `${clampedPct}%`,
                  top: 0,
                  transform: 'translateX(-50%)',
                  zIndex: 5,
                }}
              >
                {canLink ? (
                  <Link
                    href={`/pilot/${currentLeader.slug}`}
                    style={{ display: 'block' }}
                  >
                    {hud}
                  </Link>
                ) : hud}
                {/* Spitze nach unten — zentriert unter der HUD */}
                <div
                  style={{
                    position: 'absolute',
                    left: '50%',
                    bottom: -8,
                    transform: 'translateX(-50%)',
                    width: 0, height: 0,
                    borderLeft: '8px solid transparent',
                    borderRight: '8px solid transparent',
                    borderTop: '10px solid var(--award-gold)',
                    filter: 'drop-shadow(0 2px 3px rgba(0,0,0,0.6))',
                  }}
                />
              </div>
            )
          })()}

          {/* 2024-Rekord-Dreieck — R102.7.6 (Stephan: „deutlich sichtbarer Marker") */}
          {marker2024Pct != null && marker2024Pct > 0 && marker2024Pct < 100 && r2024 && (
            <div
              className="absolute pointer-events-auto cursor-help"
              data-tip={tooltip2024}
              style={{ left: `${marker2024Pct}%`, bottom: 0, transform: 'translateX(-50%)', zIndex: 4 }}
            >
              <div
                style={{
                  width: 0, height: 0,
                  borderLeft: '9px solid transparent',
                  borderRight: '9px solid transparent',
                  borderTop: '12px solid color-mix(in oklch, var(--award-cirrus-1) 50%, var(--award-gold) 50%)',
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.7))',
                }}
              />
            </div>
          )}

          {/* 2025-Rekord-Dreieck (rechtes Ende = 100%) */}
          {r2025 && (
            <div
              className="absolute pointer-events-auto cursor-help"
              data-tip={tooltip2025}
              style={{ left: '100%', bottom: 0, transform: 'translateX(-50%)', zIndex: 4 }}
            >
              <div
                style={{
                  width: 0, height: 0,
                  borderLeft: '7px solid transparent',
                  borderRight: '7px solid transparent',
                  borderTop: '10px solid var(--award-gold)',
                  filter: 'drop-shadow(0 2px 4px rgba(217,155,28,0.65))',
                }}
              />
            </div>
          )}
        </div>

        {/* Rohr-Track — R102.7.5 (Stephan-Wunsch: „massiver als zuvor"). */}
        <div
          className="relative w-full overflow-visible"
          style={{
            height: 32,
            borderRadius: 999,
            background:
              'linear-gradient(180deg, rgba(0,0,0,0.40) 0%, rgba(255,255,255,0.05) 35%, rgba(255,255,255,0.12) 62%, rgba(0,0,0,0.34) 100%)',
            border: '1px solid color-mix(in oklch, var(--award-cirrus-1) 22%, transparent)',
            boxShadow:
              'inset 0 3px 7px rgba(0,0,0,0.58), inset 0 -2px 3px rgba(255,255,255,0.07), 0 10px 20px -6px rgba(0,0,0,0.48)',
          }}
          data-tip={currentTooltip || undefined}
        >
          {/* Aktueller-Wert-Füllung mit innerem Highlight */}
          {currentPctOfCeiling != null && (
            <div
              className="absolute left-0 top-0 bottom-0 transition-[width] duration-700 ease-out"
              style={{
                width: `${Math.min(100, Math.max(2, currentPctOfCeiling))}%`,
                borderRadius: 999,
                background: beatsRecord
                  ? 'linear-gradient(180deg, color-mix(in oklch, var(--award-gold) 90%, white) 0%, var(--award-gold) 50%, color-mix(in oklch, var(--award-gold) 60%, black) 100%)'
                  : beats2024
                    ? 'linear-gradient(180deg, color-mix(in oklch, var(--award-gold) 70%, white) 0%, var(--award-gold) 55%, color-mix(in oklch, var(--award-gold) 55%, black) 100%)'
                    : 'linear-gradient(180deg, color-mix(in oklch, var(--award-gold) 55%, white) 0%, color-mix(in oklch, var(--award-gold) 80%, transparent) 55%, color-mix(in oklch, var(--award-gold) 45%, black) 100%)',
                boxShadow: beatsRecord
                  ? 'inset 0 1px 2px rgba(255,255,255,0.55), inset 0 -1px 2px rgba(0,0,0,0.30), 0 0 18px color-mix(in oklch, var(--award-gold) 60%, transparent)'
                  : 'inset 0 1px 2px rgba(255,255,255,0.40), inset 0 -1px 2px rgba(0,0,0,0.32)',
              }}
            />
          )}

          {/* 2024-Marker als deutliche Linie auf dem Rohr (Stephan-Wunsch:
              „deutlich sichtbarer Marker"). 3px breit, hell, mit Glow. */}
          {marker2024Pct != null && marker2024Pct > 0 && marker2024Pct < 100 && (
            <div
              className="absolute pointer-events-auto cursor-help"
              data-tip={tooltip2024}
              style={{
                left: `calc(${marker2024Pct}% - 1.5px)`,
                top: 1,
                bottom: 1,
                width: 3,
                background: 'color-mix(in oklch, var(--award-gold) 60%, var(--award-cirrus-1) 40%)',
                boxShadow: '0 0 6px color-mix(in oklch, var(--award-gold) 65%, transparent), 0 0 2px rgba(0,0,0,0.5)',
                zIndex: 3,
                borderRadius: 1,
              }}
            />
          )}
        </div>

        {/* r19.55.88 (Stephan-Spec 2026-08-05): Empty-State wenn KEINE Records
            fuer diese Kategorie erfasst sind (z.B. Flatland 3+H). Vorher war
            der Balken leer + keine Namen-Zeile → sah kaputt aus. */}
        {!loading && !r2024 && !r2025 && (
          <div
            className="text-center text-[10px] italic mt-2"
            style={{ color: 'var(--award-cirrus-3)', opacity: 0.75 }}
          >
            {t('wertung.recordNotYetCaptured')}
          </div>
        )}

        {/* Rekord-Halter-Zeile unter dem Rohr — R102.7.6 (Stephan-Wunsch
            2026-06-01): Namen stehen jeweils RECHTSBÜNDIG an der Position
            ihres Rekord-Markers (nicht mehr links bei 0). 2024 endet am
            2024-Pin (~marker2024Pct), 2025 endet am rechten Ende. */}
        <div className="relative w-full text-[10px] mt-1.5" style={{ color: 'var(--award-cirrus-3)', height: 16 }}>
          {r2024 && marker2024Pct != null && (
            <span
              data-tip={tooltip2024}
              className="absolute top-0 whitespace-nowrap"
              style={{
                left: `${marker2024Pct}%`,
                transform: 'translateX(-100%)',
                paddingRight: 4,
                cursor: 'help',
              }}
            >
              <span style={{ opacity: 0.7 }}>2024 · </span>
              <span style={{ color: 'var(--award-cirrus-2)', fontWeight: 600 }}>{r2024.pilot_name}</span>
              {/* r19.55.81 (Stephan-Spec 2026-08-05): Punkte-Wert des Rekords sichtbar */}
              <span style={{ opacity: 0.85, marginLeft: 4 }}>· {fmtNum(r2024.value, unit)}</span>
            </span>
          )}
          {r2025 && (
            <span
              data-tip={tooltip2025}
              className="absolute top-0 right-0 whitespace-nowrap"
              style={{ cursor: 'help' }}
            >
              <span style={{ opacity: 0.7 }}>2025 · </span>
              <span style={{ color: 'var(--award-cirrus-2)', fontWeight: 600 }}>{r2025.pilot_name}</span>
              <span style={{ opacity: 0.85, marginLeft: 4 }}>· {fmtNum(r2025.value, unit)}</span>
            </span>
          )}
        </div>
      </div>

      {/* r19.55.81/82 (Stephan-Spec 2026-08-05): Warnleuchte "new FGL-Record"
          NUR wenn aktueller Wert den HOECHSTEN Rekord aus 2024/2025 uebertrifft
          (nicht wenn nur einer von beiden geschlagen ist - der andere ist ja
          noch der gueltige All-Time-FGL-Rekord). */}
      {(() => {
        const highestRecord = Math.max(r2024?.value ?? 0, r2025?.value ?? 0)
        const isNewFglRecord = !!(currentValue && highestRecord > 0 && currentValue > highestRecord)
        if (!isNewFglRecord) return null
        const beatenYear = (r2025?.value ?? 0) >= (r2024?.value ?? 0) ? 2025 : 2024
        const beatenDiff = beatenYear === 2025 ? diffTo2025 : diffTo2024
        return (
        <div
          className="absolute top-2 right-2 pointer-events-auto"
          style={{ zIndex: 10 }}
          data-tip={t('wertung.newRecordTooltip', {
            name: currentLeader?.name ?? t('wertung.currentLeaderFallback'),
            year: beatenYear,
            diff: fmtNum(Math.abs(beatenDiff ?? 0), unit),
          })}
        >
          {/* r19.55.83 (Stephan-Spec 2026-08-05): Statt Emoji-Pille jetzt
              2 gelbe 3D-Warnleuchten links + rechts vom Text (analog
              Breaking-News-Ticker). Synchron blinkend via globale
              fgl-warning-lamp CSS. */}
          <span
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-black uppercase tracking-[.14em] whitespace-nowrap"
            style={{
              background: 'linear-gradient(135deg, rgba(251,146,60,0.18) 0%, rgba(255,215,74,0.22) 100%)',
              color: 'var(--award-gold, #ffd93b)',
              border: '1.5px solid rgba(255,215,74,0.65)',
              boxShadow: '0 0 22px rgba(255,215,74,0.35), inset 0 1px 0 rgba(255,255,255,0.18)',
              textShadow: '0 0 8px rgba(0,0,0,0.5)',
            }}
          >
            <WarningLamp size={20} />
            <span>{t('wertung.newRecordBadge')}</span>
            <WarningLamp size={20} />
          </span>
        </div>
        )
      })()}
    </div>
  )
}
